import { resetPassword } from "api/auth";
import classNames from "classnames";
import qs from "query-string";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { EyeIconOpenPassword, EyeIconPassword } from "themes/Icon";
import ButtonComp from "themes/elements/Button";
import InputComp from "themes/elements/Input";
import style from "../auth.module.scss";
import styles from "./passwordForm.module.scss";

function PasswordForm() {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("Weak");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inputUnlocked, setInputUnlocked] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const verificationRef = useRef(false);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const { token, action } = qs.parse(location.search);
  const buttonLabel =
    action === "set-password" ? "Create Password" : "Reset Password";

  useEffect(() => {
    if (token && !verificationRef.current) {
      verifyToken(token);
      verificationRef.current = true;
    }
  }, [location.search]);

  const verifyToken = async (token) => {
    if (isVerifying) return;
    setIsVerifying(true);
    try {
      const response = await resetPassword({ action: "verify", token });
      if (response) {
        setIsVerified(true);
      }
    } catch (error) {
      navigate("/login");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!isVerified) {
      toast.error("Please verify the token first.");
      return;
    }

    setLoading(true);

    if (!formData.newPassword || !formData.confirmPassword) {
      toast.warn("Please fill in all fields.");
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.warn("Entered password and confirm password are not the same.");
      setLoading(false);
      return;
    }

    if (!isPasswordValid(formData.newPassword, "user123")) {
      setLoading(false);
      return;
    }

    try {
      const { token } = qs.parse(location.search);
      const response = await resetPassword({
        action: "reset",
        token,
        new_password: formData.newPassword,
      });

      if (response) {
        toast.success(response?.message);
        navigate("/login");
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Updated evaluatePasswordStrength function
  function evaluatePasswordStrength(password) {
    let level = 0;

    // If password is empty, return "Weak"
    if (!password) {
      return { label: "Weak", level: 0 };
    }

    // 1. Contains at least 1 uppercase letter
    if (/(?=.*[A-Z])/.test(password)) level++;

    // 2. Contains at least 1 lowercase letter
    if (/(?=.*[a-z])/.test(password)) level++;

    // 3. Contains at least 1 number
    if (/(?=.*\d)/.test(password)) level++;

    // 4. Contains at least 1 special character
    if (/(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-])/.test(password)) level++;

    // 5. First and last characters should not be a number
    if (/^[^\d].*[^\d]$/.test(password)) level++;

    // 6. Password length should be at least 8 characters
    if (password.length >= 8) level++;

    // 7. No more than three identical consecutive characters
    if (!/(.)\1\1\1/.test(password)) level++;

    // Strong if all 8 conditions are met
    if (level === 7) {
      return { label: "Strong", level: 5 };
    }

    // Fair if it satisfies a majority of the conditions (for example, 5 out of 7)
    if (level >= 5) {
      return { label: "Fair", level: 3 };
    }

    // Weak if it meets less than 5 conditions
    return { label: "Weak", level: 1 };
  }

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (key === "newPassword") {
      const { label, level } = evaluatePasswordStrength(value);
      setPasswordStrength(label);
    }
  };

  const isPasswordValid = (password, userId = "") => {
    const errors = [];

    if (password.length < 8)
      errors.push("• Password must be at least 8 characters long");
    if (!/[A-Z]/.test(password))
      errors.push("• Include at least one uppercase letter (A-Z)");
    if (!/[a-z]/.test(password))
      errors.push("• Include at least one lowercase letter (a-z)");
    if (!/\d/.test(password))
      errors.push("• Include at least one number (0-9)");
    if (!/[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password))
      errors.push("• Include at least one special character");
    if (/^\d/.test(password))
      errors.push("• Password should not start with a number");
    if (/\d$/.test(password))
      errors.push("• Password should not end with a number");
    if (/(.)\1\1\1/.test(password))
      errors.push(
        "• Password must not contain more than three identical consecutive characters"
      );
    if (userId && password.toLowerCase().includes(userId.toLowerCase()))
      errors.push("• Do not include your user ID in the password");

    if (errors.length > 0) {
      toast.warn(errors.join("\n"), {
        autoClose: 8000,
        style: { whiteSpace: "pre-line" },
      });
      return false;
    }

    return true;
  };

  const handleToggleNewPassword = () => {
    const input = newPasswordRef.current?.input;
    if (input) {
      const pos = input.selectionStart ?? input.value.length;
      setShowNewPassword((prev) => !prev);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(pos, pos);
      }, 0);
    }
  };

  const handleToggleConfirmPassword = () => {
    const input = confirmPasswordRef.current?.input;
    if (input) {
      const pos = input.selectionStart ?? input.value.length;
      setShowConfirmPassword((prev) => !prev);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(pos, pos);
      }, 0);
    }
  };

  return (
    <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <input
        type="text"
        name="fake_username"
        autoComplete="username"
        style={{ display: "none" }}
        tabIndex="-1"
      />
      <input
        type="password"
        name="fake_password"
        autoComplete="new-password"
        style={{ display: "none" }}
        tabIndex="-1"
      />

      <div className="flex flex-column gap-4">
        <div className="flex flex-column gap-5">
          {/* New Password Field */}
          <InputComp
            ref={newPasswordRef}
            maxLength={30}
            label="New Password*"
            labelClass={style["input-label"]}
            placeholder="Enter a new password"
            id="npwd"
            name="npwd_field"
            type={showNewPassword ? "text" : "password"}
            value={formData.newPassword}
            onChange={(e) => handleChange("newPassword", e.target.value)}
            icon={
              <span
                style={{
                  opacity: formData.newPassword ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              >
                {!showNewPassword ? (
                  <EyeIconPassword />
                ) : (
                  <EyeIconOpenPassword />
                )}
              </span>
            }
            onIconClick={handleToggleNewPassword}
            autoComplete="new-password"
            inputMode="none"
            autoCorrect="off"
            readOnly={!inputUnlocked}
            onFocus={() => setInputUnlocked(true)}
            onCopy={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
          />

          {/* Confirm Password Field */}
          <InputComp
            ref={confirmPasswordRef}
            maxLength={30}
            label="Confirm Password*"
            labelClass={style["input-label"]}
            placeholder="Re-enter your new password"
            id="cpwd"
            name="cpwd_field"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={(e) => handleChange("confirmPassword", e.target.value)}
            icon={
              <span
                style={{
                  opacity: formData.confirmPassword ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              >
                {!showConfirmPassword ? (
                  <EyeIconPassword />
                ) : (
                  <EyeIconOpenPassword />
                )}
              </span>
            }
            onIconClick={handleToggleConfirmPassword}
            autoComplete="new-password"
            inputMode="none"
            autoCorrect="off"
            readOnly={!inputUnlocked}
            onFocus={() => setInputUnlocked(true)}
            onCopy={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
          />

          {/* Password Strength Visual */}
          <div className={styles.strengthContainerHead}>
            {formData.newPassword && (
              <>
                <div className={styles.strengthVisual}>
                  {(() => {
                    const strengthKey = passwordStrength.toLowerCase(); // Use the 3 levels: 'weak', 'fair', 'strong'
                    const strengthClass = styles[strengthKey] || styles.weak;
                    const filledBars =
                      {
                        weak: 1,
                        fair: 2,
                        strong: 3,
                      }[strengthKey] || 0; // Update for 3 levels

                    return [0, 1, 2].map((index) => (
                      <div
                        key={index}
                        className={classNames(
                          styles.segment,
                          index < filledBars && strengthClass
                        )}
                      />
                    ));
                  })()}
                </div>
                <span className={styles.strengthLabel}>{passwordStrength}</span>
              </>
            )}

            <div className={styles.rules}>
              <p className="mb-2">Your password must:</p>
              <ul>
                <li>Contain at least 1 number, uppercase & lowercase letter</li>
                <li>Be not less than 8 characters in length</li>
                <li>
                  Contain a non-numeric character in the first and last position
                </li>
                <li>
                  Contain no more than three identical consecutive characters
                </li>
                <li>Try to keep your user ID out of the password</li>
              </ul>
            </div>
          </div>

          <ButtonComp wFull height={48} className="mt-4" onClick={handleSubmit}>
            {loading
              ? buttonLabel === "Create Password"
                ? "Creating..."
                : "Resetting..."
              : buttonLabel}
          </ButtonComp>
        </div>
      </div>
    </form>
  );
}

export default PasswordForm;
